import { useRef, useState } from 'react';
import { Spinner } from '@klaro7/ui';

import { extractErrorMessage, tokenManager } from '@/lib/api';
import { downloadBlob } from '@/lib/download';
import {
  useMyPayouts,
  useConfirmPayout,
  useUploadPayoutInvoice,
  useUploadSignedReceipt,
  receiptDownloadUrl,
  fetchProformaBlob,
  fetchMySignedReceiptBlob,
  type PayoutListItem,
} from '@/features/fiscal/api';
import {
  DateRangeFilter,
  FILTER_SELECT_CLS,
  ResetFiltersButton,
  SignedReceiptBadge,
} from './filter-controls';
import { useUrlFilters } from './useUrlFilters';

const ACCEPTED_DOC_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_INVOICE_MB = 10;

function formatEur(amount: number): string {
  return amount.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Payout status badge ───────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending_confirmation: { label: 'Da confermare', cls: 'bg-blue-100 text-blue-700' },
    pending_payment: { label: 'In attesa pagamento', cls: 'bg-amber-100 text-amber-700' },
    paid: { label: 'Pagato', cls: 'bg-green-100 text-green-700' },
    cancelled: { label: 'Annullato', cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = map[status] ?? { label: status, cls: 'bg-neutral-100 text-neutral-600' };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// ── Receipt download link (occasionale) ───────────────────────────────────────

function ReceiptDownloadButton({ payout }: { payout: PayoutListItem }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setError(false);
    setDownloading(true);
    try {
      const url = receiptDownloadUrl(payout.id);
      const token = tokenManager.getAccessToken();
      // Fetch with the auth header, then trigger a download via an object URL.
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setError(true);
        return;
      }
      downloadBlob(await res.blob(), `ricevuta-${payout.receiptNumber ?? payout.id}.pdf`);
    } catch {
      // Network/unexpected failure — surface a friendly message instead of swallowing it.
      setError(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={downloading}
        className="inline-flex items-center gap-1 rounded-md bg-brand-teal/10 px-2.5 py-1 text-xs font-medium text-brand-teal hover:bg-brand-teal/20 disabled:opacity-60 transition-colors"
      >
        {downloading ? (
          <Spinner size="sm" />
        ) : (
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        )}
        Scarica ricevuta
      </button>
      {error && (
        <span className="text-[11px] text-red-600">Impossibile scaricare la ricevuta. Riprova.</span>
      )}
    </div>
  );
}

// ── Proforma download (P.IVA) ─────────────────────────────────────────────────

function ProformaDownloadButton({ payout }: { payout: PayoutListItem }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setError(false);
    setDownloading(true);
    try {
      const blob = await fetchProformaBlob(payout.id);
      downloadBlob(blob, `proforma-${payout.proformaNumber ?? payout.id}.pdf`);
    } catch {
      setError(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => void handleDownload()}
        disabled={downloading}
        className="inline-flex items-center gap-1 rounded-md bg-brand-teal/10 px-2.5 py-1 text-xs font-medium text-brand-teal hover:bg-brand-teal/20 disabled:opacity-60 transition-colors"
      >
        {downloading ? <Spinner size="sm" /> : (
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        )}
        Scarica proforma
      </button>
      {error && <span className="text-[11px] text-red-600">Impossibile scaricare. Riprova.</span>}
    </div>
  );
}

// ── Ricevuta per ritenuta firmata (occasionale) ───────────────────────────────

/**
 * Stato + caricamento della ricevuta per ritenuta firmata. Si può caricare (e
 * sostituire) finché lo staff non l'ha verificata; dopo resta solo il download.
 */
function SignedReceiptControls({ payout }: { payout: PayoutListItem }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadSignedReceipt();
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const status = payout.receiptStatus ?? (payout.signedReceipt ? 'uploaded' : 'missing');
  const canUpload = payout.status !== 'cancelled' && status !== 'verified';
  const docLabel = payout.receiptNumber != null ? `ricevuta #${payout.receiptNumber}` : 'ricevuta';
  const context = `${docLabel}, periodo ${formatDate(payout.periodFrom)} – ${formatDate(payout.periodTo)}, lordo ${formatEur(payout.totalGross)}`;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_INVOICE_MB * 1024 * 1024) {
      setError(`Il file è troppo grande (max ${MAX_INVOICE_MB} MB).`);
      return;
    }
    try {
      await upload.mutateAsync({ payoutId: payout.id, file });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDownload = async () => {
    setError(null);
    setDownloading(true);
    try {
      const blob = await fetchMySignedReceiptBlob(payout.id);
      downloadBlob(blob, payout.signedReceipt?.originalName ?? `ricevuta-firmata-${payout.receiptNumber ?? payout.id}.pdf`);
    } catch {
      setError('Impossibile scaricare il file. Riprova.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <SignedReceiptBadge status={status} />
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_DOC_TYPES}
        className="hidden"
        aria-label={`File ricevuta firmata per ${context}`}
        onChange={(e) => void handleFile(e)}
      />
      {canUpload && (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          title={`Carica la ricevuta firmata per la ${context}`}
          className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-60 transition-colors"
        >
          {upload.isPending ? <Spinner size="sm" /> : null}
          {payout.signedReceipt ? 'Sostituisci ritenuta firmata' : 'Carica ritenuta firmata'}
        </button>
      )}
      {payout.signedReceipt && (
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading}
          title={payout.signedReceipt.originalName}
          className="max-w-[160px] truncate text-[11px] text-brand-teal underline-offset-2 hover:underline disabled:opacity-60"
        >
          {downloading ? 'Download…' : `📎 ${payout.signedReceipt.originalName}`}
        </button>
      )}
      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

// ── Payout row actions (download doc, upload fattura, confirm) ────────────────

function PayoutActions({ payout }: { payout: PayoutListItem }) {
  const invoiceRef = useRef<HTMLInputElement>(null);
  const upload = useUploadPayoutInvoice();
  const confirm = useConfirmPayout();
  const [error, setError] = useState<string | null>(null);

  const isProforma = payout.documentType === 'proforma';
  const awaitingConfirm = payout.status === 'pending_confirmation';

  const handleInvoice = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_INVOICE_MB * 1024 * 1024) {
      setError(`Il file è troppo grande (max ${MAX_INVOICE_MB} MB).`);
      return;
    }
    try {
      await upload.mutateAsync({ payoutId: payout.id, file });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleConfirm = async () => {
    setError(null);
    try {
      await confirm.mutateAsync(payout.id);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col items-center gap-1.5">
      {/* Document download */}
      {isProforma
        ? payout.hasProforma && <ProformaDownloadButton payout={payout} />
        : payout.hasReceipt && <ReceiptDownloadButton payout={payout} />}

      {/* Occasionale: ricevuta per ritenuta firmata (non blocca la conferma) */}
      {payout.documentType === 'ritenuta' && <SignedReceiptControls payout={payout} />}

      {/* P.IVA invoice upload */}
      {isProforma && (
        <>
          <input ref={invoiceRef} type="file" accept={ACCEPTED_DOC_TYPES} className="hidden" onChange={(e) => void handleInvoice(e)} />
          {awaitingConfirm && (
            <button
              type="button"
              onClick={() => invoiceRef.current?.click()}
              disabled={upload.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-60 transition-colors"
            >
              {upload.isPending ? <Spinner size="sm" /> : null}
              {payout.invoice ? 'Sostituisci fattura' : 'Carica fattura'}
            </button>
          )}
          {payout.invoice && (
            <span className="max-w-[140px] truncate text-[11px] text-neutral-500" title={payout.invoice.originalName}>
              📎 {payout.invoice.originalName}
            </span>
          )}
        </>
      )}

      {/* Confirm */}
      {awaitingConfirm && (
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={confirm.isPending || (isProforma && !payout.invoice)}
          title={isProforma && !payout.invoice ? 'Carica prima la fattura' : undefined}
          className="inline-flex items-center gap-1 rounded-md bg-brand-orange px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
        >
          {confirm.isPending ? <Spinner size="sm" /> : null}
          Conferma compenso
        </button>
      )}

      {error && <span className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

/**
 * Documents + confirmation flow for the professional's payouts. Lives on the
 * Compensi page: that is where consultants look for their money, and the
 * proforma/fattura/conferma steps are what unlocks payment.
 */
const PRO_FILTER_DEFAULTS = { status: '', documentType: '', from: '', to: '' };

export function PayoutsSection() {
  const { values: filters, setFilters, reset, isDirty } = useUrlFilters(PRO_FILTER_DEFAULTS);
  const { data: payouts, isLoading } = useMyPayouts(filters);
  // Il promemoria "da confermare" guarda sempre tutti i compensi, non solo quelli filtrati.
  const { data: allPayouts } = useMyPayouts();

  // Show the cassa column only when at least one payout carries one.
  const hasCassaPayouts = payouts?.some((p) => p.cassaProfessionale > 0) ?? false;
  const toConfirm = allPayouts?.filter((p) => p.status === 'pending_confirmation').length ?? 0;
  const hasRitenuta = allPayouts?.some((p) => p.documentType === 'ritenuta') ?? false;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 px-6 py-4">
        <h2 className="text-base font-semibold text-neutral-900">Documenti e pagamenti</h2>
        <p className="mt-0.5 text-sm text-neutral-500">
          Storico dei tuoi compensi con i documenti fiscali. Scarica il documento, carica la tua fattura
          (se hai partita IVA) e conferma il compenso per mandarlo in pagamento.
          {hasRitenuta && ' Se lavori con ritenuta d\'acconto, scarica la ricevuta, firmala e ricaricala sulla riga del compenso.'}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ status: e.target.value })}
            aria-label="Filtra per stato"
            className={FILTER_SELECT_CLS}
          >
            <option value="">Tutti gli stati</option>
            <option value="pending_confirmation">Da confermare</option>
            <option value="pending_payment">In attesa pagamento</option>
            <option value="paid">Pagato</option>
            <option value="cancelled">Annullato</option>
          </select>
          <select
            value={filters.documentType}
            onChange={(e) => setFilters({ documentType: e.target.value })}
            aria-label="Filtra per documento"
            className={FILTER_SELECT_CLS}
          >
            <option value="">Tutti i documenti</option>
            <option value="ritenuta">Ricevuta (ritenuta)</option>
            <option value="proforma">Proforma</option>
          </select>
          <DateRangeFilter from={filters.from} to={filters.to} onChange={setFilters} />
          <ResetFiltersButton onClick={reset} disabled={!isDirty} />
        </div>
      </div>

      {toConfirm > 0 && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-800">
          Hai {toConfirm} {toConfirm === 1 ? 'compenso da confermare' : 'compensi da confermare'}. Finché non
          confermi, il pagamento non viene messo in lavorazione.
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !payouts || payouts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
            <svg className="h-6 w-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          </div>
          {isDirty ? (
            <>
              <p className="mt-3 text-sm font-medium text-neutral-700">Nessun compenso con questi filtri</p>
              <button type="button" onClick={reset} className="mt-1 text-xs font-medium text-brand-teal hover:underline">
                Azzera filtri
              </button>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm font-medium text-neutral-700">Nessun documento disponibile</p>
              <p className="mt-1 text-xs text-neutral-500">
                Proforma e ricevute appariranno qui alla chiusura del primo periodo di compensi.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 bg-neutral-50 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3 text-left">Periodo</th>
                <th className="px-4 py-3 text-right">Lordo</th>
                <th className="px-4 py-3 text-right">Ritenuta 20%</th>
                {hasCassaPayouts && <th className="px-4 py-3 text-right">Cassa</th>}
                <th className="px-4 py-3 text-right">Netto</th>
                <th className="px-4 py-3 text-center">N. documento</th>
                <th className="px-4 py-3 text-center">Stato</th>
                <th className="px-4 py-3 text-center">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {payouts.map((payout) => (
                <tr key={payout.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 text-neutral-700">
                    <div>{formatDate(payout.periodFrom)}</div>
                    <div className="text-xs text-neutral-400">→ {formatDate(payout.periodTo)}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-neutral-900">
                    {formatEur(payout.totalGross)}
                  </td>
                  <td className="px-4 py-3 text-right text-neutral-600">
                    {formatEur(payout.ritenuta)}
                    {payout.marcaDaBollo && (
                      <span className="ml-1 text-xs text-neutral-400" title="Marca da bollo inclusa">+bollo</span>
                    )}
                  </td>
                  {hasCassaPayouts && (
                    <td className="px-4 py-3 text-right text-neutral-600">
                      {formatEur(payout.cassaProfessionale)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-semibold text-neutral-900">
                    {formatEur(payout.netto)}
                  </td>
                  <td className="px-4 py-3 text-center text-neutral-600">
                    {payout.documentType === 'proforma'
                      ? (payout.proformaNumber != null ? `Proforma #${payout.proformaNumber}` : 'Proforma')
                      : (payout.receiptNumber != null ? `Ricevuta #${payout.receiptNumber}` : '—')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={payout.status} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <PayoutActions payout={payout} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
